import React, { useState, useEffect, useRef, useCallback } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Popover as ReactPopover, ArrowContainer } from 'react-tiny-popover';
import { useField } from 'formik';
import { FiXCircle } from 'react-icons/fi';

const PAGINATED_ZONES = gql`
  query PaginatedZonesSelect(
    $companyId: ID!
    $locationId: ID
    $paginationAttributes: PaginationAttributes!
  ) {
    paginatedZones(
      companyId: $companyId
      locationId: $locationId
      paginationAttributes: $paginationAttributes
    ) {
      zones {
        id
        name
        zoneType
      }
      pagination {
        totalRecords
      }
    }
  }
`;

export type ZoneSelectSelection = {
  value: string;
  display: string;
  zoneType?: string;
};

const zoneOptionLabel = (zone: { name: string; zoneType?: string }) =>
  zone.zoneType === 'quarantine' ? `${zone.name} (Quarantine)` : zone.name;

interface PaginatedZoneSelectProps {
  companyId: string;
  locationId?: string;
  excludeZoneIds?: string[];
  zoneTypes?: string[];
  label?: string;
  name?: string;
  form?: boolean;
  value?: string;
  onChange?: (selection: ZoneSelectSelection) => void;
  onSelect?: (selection: ZoneSelectSelection) => void;
  /**
   * Zones whose label the caller already knows but that may not be on the fetched page —
   * a zone chosen by scanning its label, for instance. Used for resolving the displayed
   * text only; the dropdown still lists whatever the paginated query returned.
   */
  knownZones?: Array<{ id: string; name: string; zoneType?: string }>;
}

const PaginatedZoneSelectControlled = ({
  companyId,
  locationId,
  excludeZoneIds = [],
  zoneTypes,
  label = 'Zone',
  value: outsideValue = '',
  onChange,
  knownZones = [],
}: Omit<PaginatedZoneSelectProps, 'form' | 'name'>) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [selectedDisplay, setSelectedDisplay] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [fetchZones, { data, loading }] = useLazyQuery(PAGINATED_ZONES, {
    fetchPolicy: 'network-only',
  });

  const doFetch = useCallback(
    (searchTerm = '') => {
      fetchZones({
        variables: {
          companyId,
          locationId,
          paginationAttributes: {
            limit: 25,
            skip: 0,
            filter: zoneTypes?.length ? [[['zoneType'], zoneTypes]] : [],
            search: searchTerm ? [['name', searchTerm]] : [],
          },
        },
      });
    },
    [companyId, locationId, zoneTypes, fetchZones],
  );

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  const zones = (data?.paginatedZones?.zones ?? []).filter(
    (z: any) => !excludeZoneIds.includes(z.id),
  );

  useEffect(() => {
    if (!outsideValue) return;
    const match =
      zones.find((z: any) => z.id === outsideValue) ||
      knownZones.find((z) => z.id === outsideValue);
    if (match) setSelectedDisplay(zoneOptionLabel(match));
  }, [outsideValue, zones, knownZones]);

  const handleInputChange = (val: string) => {
    setFilterValue(val);
    if (!popoverOpen) setPopoverOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doFetch(val), 300);
  };

  const handleSelect = (zone: any) => {
    const display = zoneOptionLabel(zone);
    setSelectedDisplay(display);
    setFilterValue(display);
    setPopoverOpen(false);
    onChange?.({ value: zone.id, display, zoneType: zone.zoneType });
  };

  const displayValue = popoverOpen ? filterValue : selectedDisplay;

  return (
    <div className="eq-select">
      <div style={{ position: 'relative' }}>
        <ReactPopover
          containerClassName="eq-popover-container eq-select-popover"
          isOpen={popoverOpen}
          positions={['bottom', 'top']}
          padding={1}
          onClickOutside={() => setPopoverOpen(false)}
          content={({ position, childRect, popoverRect }) => (
            <ArrowContainer
              position={position}
              childRect={childRect}
              popoverRect={popoverRect}
              className={`popover-arrow-container ${position}`}
              arrowClassName={`popover-arrow ${position}`}
            >
              <div className="eq-select-options">
                {loading ? (
                  <div className="option">Loading...</div>
                ) : zones.length ? (
                  zones.map((zone: any) => (
                    <div
                      key={zone.id}
                      className={`option ${outsideValue === zone.id ? 'selected' : ''}`}
                      onMouseDown={() => handleSelect(zone)}
                    >
                      {zoneOptionLabel(zone)}
                    </div>
                  ))
                ) : (
                  <div className="option">No zones found</div>
                )}
              </div>
            </ArrowContainer>
          )}
          align="start"
        >
          <div className="eq-popover-wrap">
            <input
              onChange={({ target }) => handleInputChange(target.value)}
              onFocus={() => {
                setFilterValue('');
                if (!popoverOpen) {
                  setPopoverOpen(true);
                  doFetch();
                }
              }}
              onBlur={() => {
                if (!outsideValue) {
                  setSelectedDisplay('');
                  setFilterValue('');
                }
                setPopoverOpen(false);
              }}
              value={displayValue}
              placeholder=" "
              autoComplete="off"
            />
            {!!label && <label>{label}</label>}
          </div>
        </ReactPopover>
      </div>
    </div>
  );
};

const PaginatedZoneSelectFormik = ({
  companyId,
  locationId,
  excludeZoneIds = [],
  zoneTypes,
  label = 'Zone',
  name = 'zoneId',
  onSelect,
  knownZones = [],
}: Omit<PaginatedZoneSelectProps, 'form' | 'value' | 'onChange'> & {
  name: string;
}) => {
  const [field, meta, helpers] = useField(name);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [selectedDisplay, setSelectedDisplay] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [fetchZones, { data, loading }] = useLazyQuery(PAGINATED_ZONES, {
    fetchPolicy: 'network-only',
  });

  const doFetch = useCallback(
    (searchTerm = '') => {
      fetchZones({
        variables: {
          companyId,
          locationId,
          paginationAttributes: {
            limit: 25,
            skip: 0,
            filter: zoneTypes?.length ? [[['zoneType'], zoneTypes]] : [],
            search: searchTerm ? [['name', searchTerm]] : [],
          },
        },
      });
    },
    [companyId, locationId, zoneTypes, fetchZones],
  );

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  const zones = (data?.paginatedZones?.zones ?? []).filter(
    (z: any) => !excludeZoneIds.includes(z.id),
  );

  useEffect(() => {
    if (!field.value) return;
    const match =
      zones.find((z: any) => z.id === field.value) ||
      knownZones.find((z) => z.id === field.value);
    // A scan can change the value out from under an already-filled display, so re-sync
    // whenever the resolved label differs rather than only when the display is empty.
    if (match && zoneOptionLabel(match) !== selectedDisplay) {
      setSelectedDisplay(zoneOptionLabel(match));
    }
  }, [field.value, zones, knownZones, selectedDisplay]);

  const handleInputChange = (val: string) => {
    setFilterValue(val);
    if (!popoverOpen) setPopoverOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doFetch(val), 300);
  };

  const handleSelect = (zone: any) => {
    const display = zoneOptionLabel(zone);
    setSelectedDisplay(display);
    setFilterValue(display);
    setPopoverOpen(false);
    helpers.setValue(zone.id);
    onSelect?.({ value: zone.id, display, zoneType: zone.zoneType });
  };

  const displayValue = popoverOpen ? filterValue : selectedDisplay;
  const showError = !!meta.error && !!meta.touched;

  return (
    <div className={`eq-select ${showError ? 'error' : ''}`}>
      <div style={{ position: 'relative' }}>
        <ReactPopover
          containerClassName="eq-popover-container eq-select-popover"
          isOpen={popoverOpen}
          positions={['bottom', 'top']}
          padding={1}
          onClickOutside={() => setPopoverOpen(false)}
          content={({ position, childRect, popoverRect }) => (
            <ArrowContainer
              position={position}
              childRect={childRect}
              popoverRect={popoverRect}
              className={`popover-arrow-container ${position}`}
              arrowClassName={`popover-arrow ${position}`}
            >
              <div className="eq-select-options">
                {loading ? (
                  <div className="option">Loading...</div>
                ) : zones.length ? (
                  zones.map((zone: any) => (
                    <div
                      key={zone.id}
                      className={`option ${field.value === zone.id ? 'selected' : ''}`}
                      onMouseDown={() => handleSelect(zone)}
                    >
                      {zoneOptionLabel(zone)}
                    </div>
                  ))
                ) : (
                  <div className="option">No zones found</div>
                )}
              </div>
            </ArrowContainer>
          )}
          align="start"
        >
          <div className="eq-popover-wrap">
            <input
              onChange={({ target }) => handleInputChange(target.value)}
              onFocus={() => {
                setFilterValue('');
                if (!popoverOpen) {
                  setPopoverOpen(true);
                  doFetch();
                }
              }}
              onBlur={() => {
                if (!field.value) {
                  setSelectedDisplay('');
                  setFilterValue('');
                }
                setPopoverOpen(false);
              }}
              value={displayValue}
              placeholder=" "
              autoComplete="off"
              id={name}
            />
            {!!label && <label>{label}</label>}
            {showError && (
              <div className="error-message">
                <FiXCircle /> {meta.error}
              </div>
            )}
          </div>
        </ReactPopover>
      </div>
    </div>
  );
};

const PaginatedZoneSelect = ({ form = true, ...props }: PaginatedZoneSelectProps) => {
  if (form && props.name) {
    return <PaginatedZoneSelectFormik {...props} name={props.name} />;
  }
  return <PaginatedZoneSelectControlled {...props} />;
};

export default PaginatedZoneSelect;
